import { Router } from '@angular/router';
import { NavController, Platform } from '@ionic/angular';
import { isPlatformBrowser } from '@angular/common';
import { fromEvent, merge, Subject, Subscription, take } from 'rxjs';
import { Component, ElementRef, Inject, NgZone, PLATFORM_ID, ViewChild } from '@angular/core';

import { MobileReputationService, MobileThemeService } from '@sifca-monorepo/clients';

import { reputationPicture } from '../../environments/environment';
import { PartnershipNetworkWithAccelerateType, PartnershipNetworkWithReferralType } from '@sifca-monorepo/widget-generator';

@Component({
  template: '',
})
export class MobilePartnerListComponent {
  @ViewChild('preloader', { static: false }) preloaderEl!: ElementRef;

  private userInteracted = false;
  private interactionSub!: Subscription;
  private preloaderObserver?: IntersectionObserver;
  private unsubscribeAll: Subject<void> = new Subject<void>();

  origin: string;
  isIos: boolean;
  isLoading: boolean;
  reputationPicture = reputationPicture;
  widgetSettings$ = this.reputationService.widgetSettings$;
  loyaltySettings$ = this.reputationService.loyaltySettings$;
  partners: PartnershipNetworkWithAccelerateType[] | PartnershipNetworkWithReferralType[] = [];
  
  constructor(
    protected ngZone: NgZone,
    protected router: Router,
    private platform: Platform,
    protected navCtrl: NavController,
    protected themeService: MobileThemeService,
    protected reputationService: MobileReputationService,
    @Inject(PLATFORM_ID) private platformId: any,
  ) {
    this.isIos = this.platform.is('ios');
    const nav = this.router.getCurrentNavigation();
    this.origin = nav?.extras?.state?.origin;
    this.fetchPartners();
  }

  ngAfterViewInit() {
    if (!isPlatformBrowser(this.platformId)) return;
    this.ngZone.runOutsideAngular(() => {
      const scroll$ = fromEvent(window, 'scroll');
      const wheel$ = fromEvent(window, 'wheel');
      const touch$ = fromEvent(window, 'touchstart');
      this.interactionSub = merge(scroll$, wheel$, touch$)
        .pipe(take(1))
        .subscribe(() => {
          this.userInteracted = true;
          this.setupPreloaderObserver();
        });
    });
  }

  private setupPreloaderObserver() {
    if (!this.preloaderEl) {
      setTimeout(() => this.setupPreloaderObserver(), 150);
      return;
    }

    if (this.preloaderObserver) {
      this.preloaderObserver.disconnect();
    }

    const options: IntersectionObserverInit = {
      root: null,
      threshold: 0.1,
      rootMargin: '0px 0px 150px 0px',
    };

    this.preloaderObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && this.userInteracted && !this.isLoading) {
          this.ngZone.run(() => {
            this.reputationService.isLastPartner$.pipe(take(1)).subscribe((isLast) => {
              if (!isLast) {
                this.loadMorePartners();
              } else {
                this.preloaderObserver?.disconnect();
              }
            });
          });
        }
      });
    }, options);

    this.preloaderObserver.observe(this.preloaderEl.nativeElement);
  }

  loadMorePartners() {
    this.reputationService.isLastPartner$.pipe(take(1)).subscribe((isLast) => {
      if (!isLast) {
        this.fetchPartners();
      }
    });
  }

  fetchPartners() {
    if (this.origin === 'happyHour') {
      this.reputationService.findTargetPartnersWithAccelerateSettings().subscribe((res: any) => {
        this.partners.push(...(res || []));
      });
    } else {
      this.reputationService.findTargetPartnersWithReferralSettings().subscribe((res: any) => {
        this.partners.push(...(res || []));
      });
    }
  }

  navigateTo(route: string, partner: any) {
    this.navCtrl.navigateForward(route, { state: { partner } });
  }
  
  ngOnDestroy() {
    this.partners = [];
    this.unsubscribeAll.next();
    this.unsubscribeAll.complete();
    this.partners = [];
    this.reputationService.partnerPageIndex = 0;
    if (this.interactionSub) this.interactionSub.unsubscribe();
    if (this.preloaderObserver) this.preloaderObserver.disconnect();
  }
}
